import type { ReactNode, Ref } from 'react';
import { ChevronLeft } from 'lucide-react';
import { Link } from 'react-router';
import { PeriodFilter } from '@/components/period/period-filter';
import { GranularityFilter } from '@/components/granularity-filter';
import { METRICS_TOUR_TARGET_IDS } from '@/constants/metrics-tour-target-ids';
import type { MetricsDateFilter } from '@/hooks/use-metrics-date-filter';
import { cn } from '@/lib/utils';

interface MetricsFilterBarProps {
  title: string;
  filter: MetricsDateFilter;
  backTo?: string;
  showGranularity?: boolean;
  /** Row of tabs inside the sticky block, below title and period. */
  tabs?: ReactNode;
  /** Hides period and granularity (Analytics › Pedidos has its own period). */
  hidePeriod?: boolean;
  /** Below 768px the title lives in the mobile header instead. */
  hideTitleOnMobile?: boolean;
  /** The sticky block, e.g. to measure its height. */
  stickyRef?: Ref<HTMLDivElement>;
}

export function MetricsFilterBar({
  title,
  filter,
  backTo,
  showGranularity = true,
  tabs,
  hidePeriod = false,
  hideTitleOnMobile = false,
  stickyRef,
}: MetricsFilterBarProps) {
  const row = (
    <div
      className={cn(
        'flex items-center justify-between max-md:flex-wrap max-md:gap-3',
        // Mobile Pedidos: no title (header) and no period, so no row.
        hidePeriod && hideTitleOnMobile && 'max-md:hidden'
      )}
    >
      <div
        className={cn(
          'flex items-center gap-2',
          hideTitleOnMobile && 'max-md:hidden'
        )}
      >
        {backTo && (
          <Link
            to={backTo}
            aria-label="Back"
            className="inline-flex items-center justify-center rounded-md p-1 text-muted-foreground hover:text-foreground hover:bg-accent transition-colors"
          >
            <ChevronLeft className="h-6 w-6 md:h-7 md:w-7" />
          </Link>
        )}
        <h1 className="text-2xl md:text-3xl font-bold tracking-tight">
          {title}
        </h1>
      </div>
      {!hidePeriod && (
        <div
          id={METRICS_TOUR_TARGET_IDS.filters}
          className="flex flex-row gap-2 items-center max-md:w-full max-md:min-w-0 max-md:flex-wrap"
        >
          <PeriodFilter
            fromDate={filter.fromDate ?? undefined}
            setFromDate={filter.setFromDate}
            toDate={filter.toDate ?? undefined}
            setToDate={filter.setToDate}
            predefinedDateRange={filter.predefinedDateRange}
            setPredefinedDateRange={filter.setPredefinedDateRange}
            predefinedDateRanges={filter.predefinedDateRanges}
            defaultDateRange="last_30_days"
          />
          {showGranularity && (
            <GranularityFilter
              fromDate={filter.fromDate ?? undefined}
              toDate={filter.toDate ?? undefined}
              granularity={filter.granularity}
              setGranularity={filter.setGranularity}
            />
          )}
        </div>
      )}
    </div>
  );

  const stickyClassName =
    'sticky top-0 z-50 mb-6 py-6 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 -mx-8 md:-mx-6 px-8 md:px-6';

  if (!tabs) {
    return (
      <div ref={stickyRef} className={stickyClassName}>
        {row}
      </div>
    );
  }

  return (
    <div
      ref={stickyRef}
      className={cn(stickyClassName, 'max-md:pb-0 max-md:pt-3')}
    >
      {row}
      <div
        className={cn(
          'mt-4 max-md:mt-3 max-md:-mx-8',
          hidePeriod && hideTitleOnMobile && 'max-md:mt-0'
        )}
      >
        {tabs}
      </div>
    </div>
  );
}
